import React, { useEffect, useRef } from 'react';
import PageHeader from '../common/PageHeader';
import '../../styles/Consent.css';

const Consent = (): React.ReactElement => {
  const contentSectionRef = useRef<HTMLElement | null>(null);

  // Function to check if elements are in viewport to apply fade effect
  const checkFade = () => {
    if (contentSectionRef.current) {
      const elementTop = contentSectionRef.current.getBoundingClientRect().top;
      const windowHeight = window.innerHeight;

      if (elementTop < windowHeight - 100) {
        contentSectionRef.current.classList.add('active');
      }
    }
  };

  // Set up fade effect on scroll
  useEffect(() => {
    // Initial check
    checkFade();

    // Add scroll event listener
    window.addEventListener('scroll', checkFade);

    // Clean up
    return () => {
      window.removeEventListener('scroll', checkFade);
    };
  }, []);

  return (
    <>

      <PageHeader
        title="Consent & Guidelines"
        subtitle="The foundation of our community is built on explicit consent and clear communication"
      />

      <section className="container fade-in" ref={contentSectionRef}>

        <h2 className="section-title text-center">Consent principles</h2>
        <div className="principle-grid">

          <div className="principle-card">
            <div className="principle-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <h3>Enthusiastic</h3>
            <p>Consent must be given freely and enthusiastically—never coerced or pressured.</p>
          </div>
          <div className="principle-card">
            <div className="principle-icon">
              <i className="fas fa-sync-alt"></i>
            </div>
            <h3>Ongoing</h3>
            <p>Consent is a continuous process that can be withdrawn at any time for any reason.</p>
          </div>
          <div className="principle-card">
            <div className="principle-icon">
              <i className="fas fa-comments"></i>
            </div>
            <h3>Explicit</h3>
            <p>Consent must be clearly expressed—it should never be assumed or implied.</p>
          </div>
        </div>

        <div className="policy-section">  
          <div className="rules-grid">
            <div className="rule-card">
              <div className="rule-icon">
                <i className="fas fa-redo"></i>
              </div>
              <h3>Consent Is Renewable</h3>
              <p>Even if someone said "yes" last time, don't assume anything will happen this time.</p>
            </div>
            
            <div className="rule-card">
              <div className="rule-icon">
                <i className="fas fa-volume-up"></i>
              </div>
              <h3>Saying No</h3>
              <p>Don't be afraid to say "no." It's not rude; in fact, it's respectful to be clear and honest!</p>
            </div>
            
            <div className="rule-card">
              <div className="rule-icon">
                <i className="fas fa-thumbs-down"></i>
              </div>
              <h3>Respecting No</h3>
              <p>If you hear "no," don't ask multiple times or ask for an explanation. A "no" is valid all night long. The only appropriate response is "Thanks for letting me know. If things change I'm happy having you approach me."</p>
            </div>
            
          
          </div>
        </div>

        <div className="policy-section">
                    
            <div className="rule-card">
              <div className="rule-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h3>Sober Consent</h3>
              <p>Consent must be given when all parties are in a clear state of mind—excessive substance use invalidates consent. Keep in mind that a heavily intoxicated person cannot give consent.</p>
            </div>
          </div>
        
        <div className="policy-section">
          <h2 className="section-title text-center">Group Interaction Guidelines</h2>
          <div className="rules-grid">
            <div className="rule-card">
              <div className="rule-icon">
                <i className="fas fa-eye-slash"></i>
              </div>
              <h3>Starting a Group Action</h3>
              <p>No eye contact = "No". Respect the space and leave. Always ask everyone involved for consent.</p>
            </div>
            
            <div className="rule-card">
              <div className="rule-icon">
                <i className="fas fa-user-plus"></i>
              </div>
              <h3>Inviting Others</h3>
              <p>Before waving for someone to join, make sure everyone already involved is okay with you asking.</p>
            </div>
            
            <div className="rule-card">
              <div className="rule-icon">
                <i className="fas fa-ban"></i>
              </div>
              <h3>No Solo Activities</h3>
              <p>Do not touch yourself in front of others without explicit consent.</p>
            </div>
          </div>
        </div>
        <div className="policy-section">
          <h2 className="section-title text-center">House Rules</h2>
   

          <div className="rules-grid">
            <div className="rule-card">
              <div className="rule-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3>No drinks in play-area</h3>
              <p>No drinks allowed in the play-area, especially no glass cups.</p>
            </div>
            
            <div className="rule-card">
              <div className="rule-icon">
                <i className="fas fa-hand-paper"></i>
              </div>
              <h3>Smoking area</h3>
              <p>Designated smoking area only. No smoking in the play-area.</p>
            </div>
            <div className="rule-card">
              <div className="rule-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h3>Toilets</h3>
              <p>All toilets are to be used for toilet stuff, no other activities.</p>
            </div>
          </div>
        </div>

        <div className="policy-section age-verification">
          <h2 className="section-title text-center">Age and Verification</h2>
          <div className="verification-details">
            <div className="rule-card">
              
              <div className="verification-icon">
                <i className="fas fa-id-card"></i>
              </div>
              <p>All BodySharing members must be 20+</p>
            </div>
          </div>
        </div>

        <div className="policy-section violations">
          <h2 className="section-title text-center">Violations & Consequences</h2>

          <div className="violations-grid">
            <div className="violation-level">
              <div className="level-indicator">
                <span className="level-1">Level 1</span>
              </div>
              <h3>Minor Boundary Cross</h3>
              <p>Unintentional misunderstandings that are immediately addressed when brought to attention.</p>
              <div className="consequences">
                <h4>Consequences:</h4>
                <ul>
                  <li>Verbal warning</li>
                  <li>Educational resources</li>
                </ul>
              </div>
            </div>

            <div className="violation-level">
              <div className="level-indicator">
                <span className="level-2">Level 2</span>
              </div>
              <h3>Consent Oversight</h3>
              <p>Failure to adequately check for consent or negligence in recognizing boundaries.</p>
              <div className="consequences">
                <h4>Consequences:</h4>
                <ul>
                  <li>Formal warning</li>
                  <li>Temporary suspension</li>
                  <li>Required consent training</li>
                </ul>
              </div>
            </div>

            <div className="violation-level">
              <div className="level-indicator">
                <span className="level-3">Level 3</span>
              </div>
              <h3>Severe Violation</h3>
              <p>Knowingly violating established boundaries.</p>
              <div className="consequences">
                <h4>Consequences:</h4>
                <ul>
                  <li>Permanent ban</li>
                  <li>Comprehensive review</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Consent;
